import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, of, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { Entity } from 'src/app/classes/entity.class';
import { isSelectableProject } from 'src/app/classes/project.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityId, EntityTypeCode } from 'src/app/services/entities/entity-type.class';
import { ProjectService } from 'src/app/services/project.service';
import { RealisationService } from 'src/app/services/realisation.service';
import { UserService } from 'src/app/services/user/user.service';
import { Utils } from 'src/app/utils.class';
import { AuthorisationService } from '../../../services/auth/authorisation.service';
import { Functionalities } from '../../../services/auth/functionalities';
import { LocalStorage } from '../../../storage.class';

@Component({
    selector: 'app-add-realisation-dialog',
    templateUrl: './add-realisation-dialog.component.html',
    styleUrls: ['./add-realisation-dialog.component.scss'],
    standalone: false,
})
export class AddRealisationDialogComponent extends CodaltComponent implements OnInit {
    RealisationHourtype = RealisationHourtype;
    EntityTypeCode = EntityTypeCode;

    projects: {
        projectId?: string;
        location: string;
        planningId?: number;
    }[] = [];

    selection: NewRealisation = {
        entity_id: null,
        user_id: null,
        afas_project_id: null,
        planning_id: null,
        type: null,
        time: null,
        hourtype: null,
        hiring_name: null,
    };

    employees: User[];
    entities: Entity[];

    currentPause: number;
    currentMinutes: number;

    hirings = [];

    fcProjectSearch = new FormControl<string>(null);
    fcEntities = new FormControl<number[]>(null);
    itemSearch?: string;
    fcItemSearch = new FormControl<string>(null);

    previousRealisation: Realisation;
    nextRealisation: Realisation;
    timeEnd: string;

    timeOptions: TimeOption[] = [];
    private timeOptionsSubs = new Subscription();

    creating = false;
    // Null when the colleague route does not apply, empty when the employee is already planned
    colleagueTimes: Realisation[] | null = null;
    fcColleagueTimes = new FormControl<Realisation[]>([]);

    searching = false;
    selectedIndex = 0;

    activeStep: Step = Step.Type;
    Step = Step;
    night = false;

    otherRealisationsThisDay: {
        realisations: Realisation[];
        team: Entity;
        showAsphaltTeamPerformer?: boolean;
        showProjectTeamPerformer?: boolean;
    };

    // Times others registered on the same planning and hourtype, to copy with one click
    get existingTimeOptions(): TimeOption[] {
        if (
            !this.data.useOnlyProvidedProject ||
            this.selection.type === 'planned_users' ||
            !this.selection.planning_id
        ) {
            return [];
        }
        const realisations = (this.data.realisations ?? []).filter(
            r => !r.removed && r.planning_id === this.selection.planning_id && r.hourtype === this.selection.hourtype
        );
        const options = new Map<string, TimeOption>();
        for (const realisation of realisations) {
            options.set(`${realisation.begindate}-${realisation.enddate}`, {
                begindate: Utils.newDate(realisation.begindate),
                enddate: Utils.newDate(realisation.enddate),
            });
        }
        return [...options.values()];
    }

    trackTimeOption(option: TimeOption) {
        return `${Utils.getTimeOrNull(option.begindate)}-${Utils.getTimeOrNull(option.enddate)}`;
    }

    // Explicit hoursRestricted without free hours or hour control: only plannings of the user
    readonly onlyOnPlanning =
        AuthorisationService.hasFunctionality(Functionalities.HoursRestricted) &&
        !AuthorisationService.hasFunctionality(Functionalities.Hours) &&
        !AuthorisationService.canApproveHours();
    optionalProject = false;
    // Explicit "Zonder project": afas_project_id and planning_id are both empty then
    withoutProject = false;

    constructor(
        public dialogRef: MatDialogRef<AddRealisationDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            realisations: Realisation[];
            bookdate: Date;
            userId?: number;
            useOnlyProvidedProject?: boolean;
            plannedUserIds?: number[];
            projects?: {
                projectId?: string;
                location: string;
                planningId?: number;
                begindate?: Date;
                enddate?: Date;
            }[];
        },
        private realisationService: RealisationService,
        private entityService: EntitiesService,
        private userService: UserService,
        private afprojectServicesService: ProjectService
    ) {
        super();
        this.setCurrentProjects();
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        let list = [];
        switch (this.activeStep) {
            case Step.Employee:
                list = this.employees?.filter(
                    e =>
                        !this.fcItemSearch.value ||
                        e.name.toLowerCase().indexOf(this.fcItemSearch.value?.toLowerCase()) !== -1
                );
                break;
            case Step.Project:
                list = this.projects;
                break;
        }

        if (event.key === 'Enter') {
            switch (this.activeStep) {
                case Step.Employee:
                    this.setUser(list[this.selectedIndex].id);
                    break;
                case Step.Project:
                    this.selectProject(list[this.selectedIndex].projectId, list[this.selectedIndex].planningId);
                    break;
            }
        }

        if (event.key === 'ArrowUp') {
            this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        }

        if (event.key === 'ArrowDown') {
            this.selectedIndex++;
            if (this.selectedIndex > list?.length - 1) {
                this.selectedIndex = 0;
            }
        }
    }

    private setCurrentProjects() {
        if (this.data.userId && !this.onlyOnPlanning) {
            this.subscriptions.add(
                this.afprojectServicesService.getRecentProject(+this.data.userId).subscribe(projects => {
                    this.projects = projects.data.map(p => {
                        return { projectId: p.afas_project_id, location: p.location ?? p.name };
                    });
                })
            );
            const entities$ = this.entityService.getList();
            const dayEntityIds$ = this.realisationService.getDayEntities(this.data.bookdate, +this.data.userId);

            this.subscriptions.add(
                combineLatest([entities$, dayEntityIds$]).subscribe(([entities, dayEntityIds]) => {
                    this.entities = entities
                        .filter(e => !!e.afas_employee_id)
                        .filter(
                            e =>
                                !e.enddate ||
                                Utils.newDate(e.enddate).getTime() >= Utils.newDate(this.data.bookdate).getTime()
                        );

                    let defaultEntityIds = dayEntityIds.data;
                    defaultEntityIds = this.entities.filter(e => defaultEntityIds.indexOf(e.id) !== -1).map(e => e.id);
                    if (defaultEntityIds.length === 0 && !!this.data.userId) {
                        defaultEntityIds = this.entities
                            .filter(e => e.driver_user_id === this.data.userId)
                            .map(e => e.id);
                    }
                    this.fcEntities.setValue(defaultEntityIds);
                })
            );
        } else {
            if (this.data.projects?.length) {
                this.projects = [...this.data.projects];
                return;
            }

            this.projects = [];
            const projects = [...new Set(this.data.realisations?.map(r => Utils.realisationAfasProjectId(r)))].filter(
                p => !!p
            );
            projects.forEach(project => {
                const realisation = this.data.realisations
                    .filter(p => !p.planning?.is_transport)
                    .sort((a, b) => (a.planning_id ? 0 : 1) - (b.planning_id ? 0 : 1))
                    .find(r => r.planning?.afas_project_id === project);
                const afasProject = this.data.realisations.find(r => r.project?.afas_project_id === project);
                this.projects.push({
                    projectId: project,
                    location: realisation?.planning?.location ?? afasProject?.project?.name,
                    planningId: realisation?.planning?.id ?? null,
                });
            });
        }
    }

    setActiveStep(step: Step) {
        if (step === Step.Hourtype && !this.selection.user_id && this.selection.type !== 'planned_users') {
            return;
        }
        if (
            step === Step.Project &&
            ((!this.selection.user_id && !this.selection.entity_id) ||
                !this.selection.type ||
                !this.selection.hourtype ||
                NON_PROJECT_HOURTYPES.indexOf(this.selection.hourtype) !== -1 ||
                (this.selection.type?.substr(0, 6) === 'hiring' && !this.selection.hiring_name))
        ) {
            return;
        }
        if (step === Step.Employee && !this.selection.type) {
            return;
        }
        if (
            step === Step.Vehicle &&
            !this.colleagueTimes?.length &&
            (!this.selection.type || !this.selection.afas_project_id || !this.selection.hourtype || !this.data.userId)
        ) {
            return;
        }

        this.activeStep = step;
    }

    setType(type: NewRealisationType) {
        // With a given user the type is only set on init, setCurrentProjects loads the lists then
        if (!this.data.userId) {
            this.loadTypeOptions(type);
        }
        this.selection.type = type;
        this.colleagueTimes = null;
        this.selection.user_id = this.data.userId;
        this.definePrevNextRealisation();
        this.selection.entity_id = null;
        this.selection.hiring_name = null;
        if (type === 'hiring_user') {
            this.selection.entity_id = EntityId.HiringEmployee;
        }
        if (type === 'hiring_material') {
            this.selection.entity_id = EntityId.HiringMaterials;
        }

        if (type === 'user' || type === 'planned_users') {
            this.selection.hourtype = null;
            this.goNext();
        } else {
            this.setHourtype(RealisationHourtype.worktime);
        }
    }

    setTime(time: TimeOption) {
        this.selection.time = time;
        this.goNext();
    }

    setHourtype(hourtype: RealisationHourtype) {
        this.selection.hourtype = hourtype;
        this.optionalProject = OPTIONAL_PROJECT_HOURTYPES.includes(hourtype);
        // The time options are recalculated for this hourtype
        this.selection.time = null;
        this.withoutProject = false;

        if (NON_PROJECT_HOURTYPES.includes(hourtype)) {
            this.selection.afas_project_id = null;
            this.selection.planning_id = null;

            if (this.data.userId) {
                this.selection.user_id = this.data.userId;
                this.definePrevNextRealisation();
                this.selection.entity_id = null;
            }
        }

        this.goNext();
        this.createTimeOptions();
    }

    // First required step that is not filled in yet, also when steps were skipped via the stepper
    private missingStep(): Step | undefined {
        const value = this.selection;

        if (!value.type) {
            return Step.Type;
        }
        if (
            (value.type === 'user' && !value.user_id) ||
            (value.type === 'entity' && !value.entity_id) ||
            (value.type.startsWith('hiring') && !value.hiring_name)
        ) {
            return Step.Employee;
        }
        if (!value.hourtype) {
            return Step.Hourtype;
        }
        if (
            !NON_PROJECT_HOURTYPES.includes(value.hourtype) &&
            !value.afas_project_id &&
            !value.planning_id &&
            !(this.withoutProject && this.optionalProject)
        ) {
            return Step.Project;
        }
        if (value.time === null) {
            return Step.Time;
        }
    }

    // Automatic navigation after every choice
    private goNext(): void {
        const step = this.missingStep();
        // When this flag is set, the caller already decided that the dialog
        // must stay on the provided project/planning instead of offering a choice.
        // Without project search a single planning leaves nothing to choose either.
        const singlePlanning = this.onlyOnPlanning && !this.optionalProject && this.projects.length === 1;
        const injectedProject =
            (this.data.useOnlyProvidedProject ? this.data.projects?.[0] : null) ??
            (singlePlanning ? this.projects[0] : null);

        if (this.colleagueTimes) {
            this.activeStep = Step.Confirm;
        } else if (step === Step.Project && (injectedProject?.projectId || injectedProject?.planningId)) {
            this.selectProject(injectedProject.projectId, injectedProject.planningId);
        } else if (step !== undefined) {
            this.activeStep = step;
        } else if (
            this.selection.type === 'user' &&
            !this.optionalProject &&
            !NON_PROJECT_HOURTYPES.includes(this.selection.hourtype)
        ) {
            // Linking vehicles is optional, the user confirms with "Toevoegen"
            this.activeStep = Step.Vehicle;
        } else {
            this.createRealisation();
        }
    }

    createTimeOptions() {
        this.timeOptions = [];
        this.currentPause = 0;
        this.currentMinutes = 0;
        // A slow response for an earlier selection must not overwrite newer options
        this.timeOptionsSubs.unsubscribe();

        const addDayNightOptions = (workMinutesToCreate?: number) => {
            const dayBegin = Utils.setTimeNewDate(this.data.bookdate, 7, 0);

            this.timeOptions.push({
                begindate: dayBegin,
                enddate: Utils.addMinutes(dayBegin, workMinutesToCreate ?? 60 * 8),
            });

            const nightBegin = Utils.setTimeNewDate(this.data.bookdate, 20, 30);

            this.timeOptions.push({
                begindate: nightBegin,
                enddate: Utils.addMinutes(nightBegin, workMinutesToCreate ?? 60 * 8),
            });
        };

        // Gaps between consecutive rows (sorted by begin), e.g. after moving a begin for waiting time
        const gapOptions = (rows: Realisation[]): TimeOption[] =>
            rows
                .slice(1)
                .flatMap((row, index) =>
                    Utils.getTimeOrNull(row.begindate) > Utils.getTimeOrNull(rows[index].enddate)
                        ? [{ begindate: Utils.newDate(rows[index].enddate), enddate: Utils.newDate(row.begindate) }]
                        : []
                )
                .filter(gap => !this.timeOptions.some(option => Utils.sameDateRange(option, gap)));

        if (this.selection.user_id) {
            this.subscriptions.add(
                (this.timeOptionsSubs = combineLatest([
                    this.realisationService.getUserDaySchedule(this.data.bookdate, this.selection.user_id),
                    this.realisationService.getUserDayTotals(this.data.bookdate, this.selection.user_id),
                ]).subscribe(([userDaySchedule, totals]) => {
                    this.currentPause = +(totals?.dayTotal?.pause ?? 0);
                    this.currentMinutes = +(totals?.dayTotal?.minutes ?? 0);
                    const remaining = 480 - (this.currentMinutes - this.currentPause);
                    const realisations = (userDaySchedule.data.realisations ?? [])
                        .filter(r => {
                            return (
                                !r.removed &&
                                // Short non-conflicting registrations (< 6 hours) still block the time
                                (!NON_CONFLICTING_HOURTYPES.includes(r.hourtype) ||
                                    Utils.minuteDuration(r.enddate, r.begindate) < 6 * 60) &&
                                r.hiring_name === this.selection.hiring_name
                            );
                        })
                        .sort((a, b) => Utils.getTimeOrNull(a.begindate) - Utils.getTimeOrNull(b.begindate));
                    const pauseAdjusted = !!userDaySchedule.data.realisations?.find(r => !!r.pause_adjusted);

                    if (this.selection.planning_id) {
                        const planning = this.data.realisations.find(
                            r => r.planning_id === this.selection.planning_id
                        )?.planning;
                        // no realisations yet (first entry): use the planning times passed by the caller
                        const mainPlanning = planning
                            ? this.data.realisations.find(
                                  r =>
                                      r.planning?.id === planning.id &&
                                      r.planning_has_entity_id === planning.planning_has_id
                              )
                            : this.data.projects?.find(p => p.planningId === this.selection.planning_id);
                        // Only keep planning times when > 2 hours
                        if (
                            mainPlanning &&
                            Utils.minuteDuration(mainPlanning.enddate, mainPlanning.begindate) > 60 * 2
                        ) {
                            this.timeOptions.push({
                                begindate: Utils.newDate(mainPlanning.begindate),
                                enddate: Utils.newDate(mainPlanning.enddate),
                            });
                        }
                    }

                    this.timeOptions.push(...gapOptions(realisations));

                    let workMinutesToCreate = remaining;
                    if (!pauseAdjusted && this.environment.worktimeHourTypes.includes(this.selection.hourtype)) {
                        // Pause depends on the total, so iterate until the end time settles
                        for (let i = 0; i < 3; i++) {
                            workMinutesToCreate =
                                480 -
                                this.currentMinutes +
                                this.realisationService.calculatePause(
                                    this.selection.hourtype,
                                    userDaySchedule.data.realisations ?? [],
                                    this.currentMinutes,
                                    workMinutesToCreate
                                );
                        }
                    }

                    // Already worked a full day: still suggest at least an hour instead of an end before the begin
                    workMinutesToCreate = Math.max(60, workMinutesToCreate);

                    if (realisations.length > 0) {
                        this.nextRealisation = realisations[0];
                        const beginTimeStart = Utils.addMinutes(
                            this.nextRealisation.begindate,
                            workMinutesToCreate,
                            true
                        );
                        if (beginTimeStart.getDate() === new Date(this.data.bookdate).getDate()) {
                            this.timeOptions.push({
                                begindate: beginTimeStart,
                                enddate: this.nextRealisation.begindate,
                            });
                        }

                        this.previousRealisation = realisations[realisations.length - 1];
                        this.timeOptions.push({
                            begindate: Utils.newDate(this.previousRealisation.enddate),
                            enddate: Utils.addMinutes(this.previousRealisation.enddate, workMinutesToCreate),
                        });

                        if (workMinutesToCreate > 90) {
                            this.timeOptions.push({
                                begindate: Utils.newDate(this.previousRealisation.enddate),
                                enddate: Utils.addMinutes(this.previousRealisation.enddate, 60),
                            });
                        }
                    }

                    addDayNightOptions(workMinutesToCreate);
                }))
            );
        } else {
            if (this.selection.type === 'planned_users') {
                const userRealisations = this.data.plannedUserIds.map(userId =>
                    this.data.realisations
                        .filter(
                            r => r.user_id === userId && !r.removed && Utils.matchesDate(r.bookdate, this.data.bookdate)
                        )
                        .sort((a, b) => Utils.realisationSort(a) - Utils.realisationSort(b))
                );
                const plannedRealisations = userRealisations.flat();

                if (plannedRealisations.length) {
                    // Before the earliest begin and after the latest end, so the new rows overlap nobody
                    const workMinutes = Math.max(
                        60,
                        Math.min(
                            ...userRealisations.map(
                                rows =>
                                    480 - rows.reduce((sum, r) => sum + Utils.minuteDuration(r.enddate, r.begindate), 0)
                            )
                        )
                    );
                    const firstBegin = new Date(
                        Math.min(...plannedRealisations.map(r => Utils.getTimeOrNull(r.begindate)))
                    );
                    const lastEnd = new Date(Math.max(...plannedRealisations.map(r => Utils.getTimeOrNull(r.enddate))));
                    const before = Utils.addMinutes(firstBegin, workMinutes, true);

                    if (Utils.matchesDate(before, this.data.bookdate)) {
                        this.timeOptions.push({ begindate: before, enddate: firstBegin });
                    }
                    this.timeOptions.push({ begindate: lastEnd, enddate: Utils.addMinutes(lastEnd, workMinutes) });

                    // Only gaps every planned user has, so the new rows overlap nobody
                    const userGaps = userRealisations.map(gapOptions);
                    this.timeOptions.push(
                        ...userGaps[0].filter(gap =>
                            userGaps.every(gaps => gaps.some(option => Utils.sameDateRange(option, gap)))
                        )
                    );
                    return;
                }
            }

            addDayNightOptions();
        }
    }

    ngOnInit(): void {
        if (this.data.userId) {
            this.setType('user');
        }

        this.subscriptions.add(
            this.fcItemSearch.valueChanges.pipe(debounceTime(200)).subscribe(search => {
                this.loadHiringNames(this.selection.type, search);
            })
        );

        let afasSubs = new Subscription();
        this.subscriptions.add(
            this.fcProjectSearch.valueChanges.pipe(debounceTime(200)).subscribe(search => {
                afasSubs.unsubscribe();
                if (search) {
                    this.searching = true;
                    afasSubs = this.afprojectServicesService.searchProjects(search).subscribe(
                        projects => {
                            this.projects = projects
                                ?.filter(isSelectableProject)
                                .map(p => ({ projectId: p.afas_project_id, location: p.name }));
                            this.selectedIndex = 0;
                            this.searching = false;
                        },
                        () => {
                            this.searching = false;
                        }
                    );
                } else {
                    // The cancelled search above would otherwise leave the loader visible
                    this.searching = false;
                    this.setCurrentProjects();
                }
            })
        );

        this.subscriptions.add(
            this.fcEntities.valueChanges.subscribe(() => {
                const getSorter = (entity: Entity) =>
                    (this.fcEntities.value.indexOf(entity.id) !== -1 ? 100000 : 0) + entity.id;

                this.entities = this.entities.sort((a, b) => getSorter(b) - getSorter(a));
            })
        );
    }

    private loadTypeOptions(type: NewRealisationType) {
        this.subscriptions.add(
            this.entityService.getList().subscribe(entities => {
                const belongsToEntityType = entities.filter(e => {
                    return e.default_employees?.includes(LocalStorage.user.id);
                });
                const entityTypeIds = belongsToEntityType.map(e => e.entitytypes.map(ee => ee.key)).flat();
                this.entities = entities
                    .filter(e => {
                        return (
                            !!e.afas_employee_id &&
                            (entityTypeIds.length === 0 ||
                                e.entitytypes.filter(
                                    et => !et.belongs_to || entityTypeIds.includes(String(et.belongs_to))
                                ).length > 0)
                        );
                    })
                    .filter(
                        e =>
                            !e.enddate ||
                            Utils.newDate(e.enddate).getTime() >= Utils.newDate(this.data.bookdate).getTime()
                    );
            })
        );
        if (type === 'user') {
            this.subscriptions.add(
                this.userService.getList(false, false).subscribe(users => {
                    const myRoles = LocalStorage.user.roles.map(r => r.key);
                    const myId = LocalStorage.user.id;
                    const canAddUser = (u: User) =>
                        u.id === myId || u.hours_checked_by === myId || myRoles.includes(u.hours_checked_by_role);

                    if (AuthorisationService.hasRole(this.environment.roles.HourCheckRestricted)) {
                        this.employees = users.filter(u => canAddUser(u));
                    } else {
                        this.employees = users.filter(
                            u => (!u.hours_checked_by && !u.hours_checked_by_role) || canAddUser(u)
                        );
                    }
                    this.selectedIndex = 0;
                })
            );
        }
        this.loadHiringNames(type, this.fcItemSearch.value);
    }

    private loadHiringNames(type: NewRealisationType, search: string) {
        if (type === 'hiring_material' || type === 'hiring_user') {
            this.realisationService.hiringnames(type, search).subscribe(result => {
                this.hirings = result.data;
                this.selectedIndex = 0;
            });
        }
    }

    private definePrevNextRealisation() {
        // Find the previous realisation for the current user on the current bookdate excluding removed realisations
        const matchingRealisations = this.data.realisations
            ?.filter(r => {
                return (
                    r.user_id === (this.selection.user_id ?? this.data.userId) &&
                    Utils.matchesDate(r.bookdate, this.data.bookdate) &&
                    !r.removed &&
                    !NON_CONFLICTING_HOURTYPES.includes(r.hourtype) &&
                    r.hiring_name === this.selection.hiring_name
                );
            })
            .sort((a, b) => Utils.realisationSort(a) - Utils.realisationSort(b));
        if (matchingRealisations?.length > 0) {
            this.nextRealisation = matchingRealisations[0];

            this.previousRealisation = matchingRealisations[matchingRealisations.length - 1];
        } else {
            this.nextRealisation = null;
            this.previousRealisation = null;
        }
    }

    selectProject(projectId?: string, planningId?: number) {
        this.selection.afas_project_id = projectId;
        this.selection.planning_id = planningId;
        this.withoutProject = !projectId && !planningId;
        this.selection.time = null;
        this.goNext();
    }

    setEntity(entity: Entity) {
        this.selection.entity_id = entity.id;
        this.selection.user_id = null;
        this.selection.afas_project_id = null;
        this.selection.planning_id = null;
        this.selection.time = null;
        this.definePrevNextRealisation();
        this.goNext();
    }

    setUser(userId: number) {
        this.subscriptions.add(
            this.realisationService.getUserDay(userId, this.data.bookdate).subscribe(userDay => {
                this.otherRealisationsThisDay = userDay.data;

                const team = this.otherRealisationsThisDay.team;

                this.otherRealisationsThisDay.showAsphaltTeamPerformer =
                    !team || !!team?.entitytypes.find(et => et.key === EntityTypeCode.AsfaltTeam);

                this.otherRealisationsThisDay.showProjectTeamPerformer =
                    !team || !!team?.entitytypes.find(et => et.key === EntityTypeCode.ProjectTeam);
            })
        );

        this.selection.user_id = userId;
        this.definePrevNextRealisation();
        this.selection.entity_id = null;
        // Hourtype and time suggestions depend on the user, so always choose the hourtype again
        this.selection.hourtype = null;

        this.defineColleagueTimes();
        this.goNext();
    }

    // In the day report an employee joins the whole planning item: copy every hourtype group of the colleagues
    private defineColleagueTimes() {
        this.colleagueTimes = null;
        this.fcColleagueTimes.setValue([]);
        const injectedProject = this.data.useOnlyProvidedProject ? this.data.projects?.[0] : null;

        if (this.selection.type !== 'user' || !injectedProject?.planningId) {
            return;
        }

        // Registrations on another date of this planning, e.g. after the planning moved, also count as planned
        if (
            this.data.realisations.some(
                realisation =>
                    realisation.user_id === this.selection.user_id &&
                    !realisation.removed &&
                    realisation.planning_id === injectedProject.planningId &&
                    !Utils.matchesDate(realisation.bookdate, this.data.bookdate)
            )
        ) {
            this.colleagueTimes = [];
            return;
        }

        const userCounts = new Map<string, number>();
        const groupTimes = new Map<string, Realisation>();
        const ownKeys = new Set<string>();
        this.data.realisations
            .filter(
                realisation =>
                    realisation.user_id &&
                    !realisation.removed &&
                    realisation.planning_id === injectedProject.planningId &&
                    Utils.matchesDate(realisation.bookdate, this.data.bookdate)
            )
            .sort((a, b) => Utils.realisationSort(a) - Utils.realisationSort(b))
            .forEach(realisation => {
                // A user's second row of the same hourtype is its own group, like in the day report
                const userKey = `${realisation.hourtype}-${realisation.user_id}`;
                const index = userCounts.get(userKey) ?? 0;
                userCounts.set(userKey, index + 1);

                const key = `${realisation.hourtype}-${index}`;
                const group = groupTimes.get(key);
                groupTimes.set(key, {
                    hourtype: realisation.hourtype,
                    begindate: new Date(
                        Math.min(
                            Utils.getTimeOrNull(group?.begindate) ?? Infinity,
                            Utils.getTimeOrNull(realisation.begindate)
                        )
                    ),
                    enddate: new Date(
                        Math.max(
                            Utils.getTimeOrNull(group?.enddate) ?? -Infinity,
                            Utils.getTimeOrNull(realisation.enddate)
                        )
                    ),
                } as Realisation);

                if (realisation.user_id === this.selection.user_id) {
                    ownKeys.add(key);
                }
            });

        if (!groupTimes.size) {
            return;
        }

        // Only the groups the employee does not have yet, so adding someone twice creates no duplicates
        this.colleagueTimes = [...groupTimes].filter(([key]) => !ownKeys.has(key)).map(([, times]) => times);
        this.fcColleagueTimes.setValue([...this.colleagueTimes]);
    }

    saveColleagueTimes() {
        const injectedProject = this.data.projects[0];
        const entityId = this.fcEntities.value?.[0];
        this.creating = true;
        const realisations$ = this.fcColleagueTimes.value.map(times => {
            const realisation = {
                ...new Realisation(),
                ...times,
                planning_id: injectedProject.planningId,
                afas_project_id: injectedProject.projectId,
                bookdate: this.data.bookdate,
            };
            return this.realisationService.saveRealisation({ ...realisation, user_id: this.selection.user_id }).pipe(
                switchMap(result =>
                    entityId
                        ? this.realisationService.saveRealisation({
                              ...realisation,
                              user_id: null,
                              entity_id: entityId,
                              parent_realisation_id: result.data.realisation.id,
                          })
                        : of(result)
                )
            );
        });

        // Also close on error: a retry would duplicate the rows that did save, reopening shows them
        combineLatest(realisations$).subscribe({
            next: () => this.dialogRef.close(),
            error: () => this.dialogRef.close(),
        });
    }

    createRealisation() {
        if (this.creating) {
            return;
        }

        const missingStep = this.missingStep();
        if (missingStep !== undefined) {
            this.activeStep = missingStep;
            return;
        }

        this.creating = true;
        const realisation = new Realisation();
        realisation.user_id = this.selection.user_id;
        realisation.entity_id = this.selection.entity_id;
        realisation.afas_project_id = this.selection.afas_project_id;
        realisation.hourtype = this.selection.hourtype ?? RealisationHourtype.worktime;
        realisation.bookdate = this.data.bookdate;
        realisation.hiring_name = this.selection.hiring_name;

        if (this.selection.planning_id) {
            realisation.planning_id = this.selection.planning_id;
        }
        realisation.begindate = this.selection.time.begindate;
        realisation.enddate = this.selection.time.enddate;

        if (this.selection.type === 'planned_users') {
            const realisations$ = this.data.plannedUserIds.map(user_id =>
                this.realisationService.saveRealisation({ ...realisation, user_id })
            );

            // Also close on error: a retry would duplicate the rows that did save
            combineLatest(realisations$).subscribe({
                next: () => this.dialogRef.close(),
                error: () => this.dialogRef.close(),
            });
            return;
        }

        this.realisationService.saveRealisation(realisation).subscribe(
            result => {
                if (realisation.hourtype === RealisationHourtype.worktime && this.fcEntities.value?.length > 0) {
                    const realisations = [];
                    this.fcEntities.value.forEach(entityId => {
                        const entityRealisation = structuredClone(realisation);
                        entityRealisation.entity_id = entityId;
                        entityRealisation.user_id = null;
                        entityRealisation.parent_realisation_id = result.data.realisation.id;
                        entityRealisation.hourtype = RealisationHourtype.worktime;
                        realisations.push(entityRealisation);
                    });

                    const realisations$ = realisations.map(r => this.realisationService.saveRealisation(r));
                    // The user row is saved, so also close when a vehicle row fails
                    combineLatest(realisations$).subscribe({
                        next: () => this.dialogRef.close(result.data.realisation),
                        error: () => this.dialogRef.close(result.data.realisation),
                    });
                } else {
                    this.dialogRef.close(result.data.realisation);
                }
            },
            () => {
                this.creating = false;
            }
        );
    }

    setHiring(name: string) {
        this.selection.hiring_name = name;
        this.selection.user_id = null;
        this.selection.afas_project_id = null;
        this.selection.planning_id = null;
        this.selection.time = null;
        this.definePrevNextRealisation();
        this.goNext();
    }
}

type TimeOption = { begindate: Date; enddate: Date };

type NewRealisationType = 'entity' | 'user' | 'hiring_material' | 'hiring_user' | 'planned_users';

type NewRealisation = Pick<
    Realisation,
    'user_id' | 'entity_id' | 'afas_project_id' | 'planning_id' | 'hourtype' | 'hiring_name'
> & {
    type: NewRealisationType;
    time: TimeOption;
};

export const NON_PROJECT_HOURTYPES = [
    RealisationHourtype.day_off,
    RealisationHourtype.frost,
    RealisationHourtype.illness,
    RealisationHourtype.short_absence,
    RealisationHourtype.paid_leave,
    RealisationHourtype.special_leave,
];
export const OPTIONAL_PROJECT_HOURTYPES = [
    RealisationHourtype.education,
    RealisationHourtype.sleep,
    RealisationHourtype.office,
];

export const NON_CONFLICTING_HOURTYPES = [
    RealisationHourtype.paid_leave,
    RealisationHourtype.sleep,
    RealisationHourtype.bank_holiday,
];

export enum Step {
    Project,
    Hourtype,
    Employee,
    Type,
    Vehicle,
    Time,
    Confirm,
}
